import { Download, MoreHorizontal, RefreshCw } from "lucide-react";
import {
  useRef,
  useState,
  type ComponentProps,
  type FC,
  type ReactNode
} from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import { DownloadPopover } from "./DownloadPopover";

type DownloadProps = Omit<
  ComponentProps<typeof DownloadPopover>,
  "anchor" | "open" | "onOpenChange"
>;

type Props = {
  /** "Case Files": names the button ("Case Files Actions"). */
  name: string;
  testId: string;
  /** The tree has something in it; Download and the tree's items need it. */
  hasItems: boolean;
  /** Download opens this popover under the button. */
  download: DownloadProps;
  /** Reloads this pane's tree. Stays available when the tree is empty. */
  onRefresh: () => void;
  /** The tree's own items, after Download and Refresh. */
  children?: ReactNode;
  /** Tree actions before Download, such as Select All. */
  leading?: ReactNode;
  /** Toolbar actions that didn't fit the header, above everything else. */
  overflow?: ReactNode;
};

/**
 * The "…" at the end of a tree pane's toolbar: Download, Refresh and the
 * tree's own options. Items that need files are disabled while the tree is
 * empty, so the menu always reads the same. Refresh stays available.
 * Toolbar actions a narrow header can't show come first.
 */
export const TreeOverflowMenu: FC<Props> = ({
  name,
  testId,
  hasItems,
  download,
  onRefresh,
  children,
  leading,
  overflow
}) => {
  const [downloadOpen, setDownloadOpen] = useState(false);
  // The popover opens once the menu has closed and given focus back, or the
  // returning focus would dismiss it straight away.
  const pendingDownload = useRef(false);

  return (
    <DropdownMenu>
      <DownloadPopover
        {...download}
        anchor={
          <span className="inline-flex">
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    aria-label={`${name} Actions`}
                    data-testid={testId}
                  >
                    <MoreHorizontal className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">More Actions</TooltipContent>
            </Tooltip>
          </span>
        }
        open={downloadOpen}
        onOpenChange={setDownloadOpen}
      />
      <DropdownMenuContent
        align="end"
        className="w-64 [&_[role^=menuitem]]:whitespace-nowrap"
        data-testid={`${testId}-menu`}
        onCloseAutoFocus={(e) => {
          if (!pendingDownload.current) return;
          pendingDownload.current = false;
          e.preventDefault();
          setDownloadOpen(true);
        }}
      >
        {overflow ? (
          <>
            <div role="group" aria-label="Toolbar Actions">
              {overflow}
            </div>
            <DropdownMenuSeparator />
          </>
        ) : null}
        {leading}
        <DropdownMenuItem
          disabled={!hasItems}
          onSelect={() => {
            pendingDownload.current = true;
          }}
          data-testid={`${testId}-download`}
        >
          <Download />
          Download…
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => onRefresh()}
          data-testid={`${testId}-refresh`}
        >
          <RefreshCw />
          Refresh
        </DropdownMenuItem>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
